import { useEffect, useState } from "react";
import { api, formatarErroApi } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function GerenciarInformativos() {
  const [lista, setLista] = useState([]);
  const [modal, setModal] = useState(null);

  const carregar = async () => {
    const { data } = await api.get("/informativos");
    setLista(data);
  };
  useEffect(() => { carregar(); }, []);

  const excluir = async (id) => {
    if (!confirm("Excluir informativo?")) return;
    try {
      await api.delete(`/informativos/${id}`);
      toast.success("Excluído");
      carregar();
    } catch (e) { toast.error(formatarErroApi(e)); }
  };

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-informativos">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Comunicação</div>
            <h1 className="font-heading text-4xl font-bold uppercase mt-1">Informativos</h1>
          </div>
          <button
            onClick={() => setModal({})}
            className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest bg-verdebrasil hover:bg-verdebrasil-dark flex items-center gap-2"
            data-testid="btn-novo-informativo"
          >
            <Plus className="w-4 h-4" /> Novo
          </button>
        </div>

        <div className="space-y-3">
          {lista.length === 0 && (
            <div className="surface-card rounded-2xl p-10 text-center text-white/50">Nenhum informativo cadastrado.</div>
          )}
          {lista.map((inf) => (
            <div key={inf.id} className="surface-card rounded-2xl p-6 flex items-start justify-between gap-4" data-testid={`linha-inf-${inf.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold ${inf.publicado ? "bg-verdebrasil/20 text-verdebrasil" : "bg-white/10 text-white/60"}`}>{inf.publicado ? "Publicado" : "Rascunho"}</span>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">
                    {inf.publicadoEm ? new Date(inf.publicadoEm).toLocaleString("pt-BR") : ""}
                  </span>
                </div>
                <div className="font-heading uppercase font-bold text-xl mt-2">{inf.titulo}</div>
                <p className="text-sm text-white/70 mt-1 line-clamp-2">{inf.conteudo}</p>
              </div>
              <div className="flex flex-col gap-2 text-xs font-bold uppercase tracking-widest">
                <button onClick={() => setModal(inf)} className="text-amarelobrasil hover:text-white" data-testid={`btn-editar-inf-${inf.id}`}>Editar</button>
                <button onClick={() => excluir(inf.id)} className="text-red-400 hover:text-red-300" data-testid={`btn-excluir-inf-${inf.id}`}>Excluir</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <ModalInformativo
          inf={modal}
          onFechar={() => setModal(null)}
          onSalvo={() => { setModal(null); carregar(); }}
        />
      )}
    </LayoutAutenticado>
  );
}

function ModalInformativo({ inf, onFechar, onSalvo }) {
  const [form, setForm] = useState({
    titulo: inf.titulo || "",
    conteudo: inf.conteudo || "",
    publicado: !!inf.publicado,
  });
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      if (inf.id) {
        await api.patch(`/informativos/${inf.id}`, form);
      } else {
        await api.post("/informativos", form);
      }
      toast.success("Salvo");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4" data-testid="modal-informativo">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-2xl">{inf.id ? "Editar" : "Novo"} Informativo</h2>

        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Título</label>
          <input
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            required
            data-testid="input-inf-titulo"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Conteúdo</label>
          <textarea
            rows={6}
            value={form.conteudo}
            onChange={(e) => setForm({ ...form, conteudo: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            required
            data-testid="input-inf-conteudo"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.publicado}
            onChange={(e) => setForm({ ...form, publicado: e.target.checked })}
            className="accent-verdebrasil w-4 h-4"
            data-testid="checkbox-inf-publicado"
          />
          Publicar imediatamente
        </label>
        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-inf"
        >
          {enviando ? "Salvando..." : "Salvar"}
        </button>
      </form>
    </div>
  );
}
